import { AuditReportData } from '@app/types/auditReport';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

interface AuditReportFormProps {
  data: AuditReportData;
  onFieldChange: (field: string, value: any) => void;
  onNestedFieldChange: (section: string, field: string, value: any) => void;
}

export function AuditReportForm({ data, onFieldChange, onNestedFieldChange }: AuditReportFormProps) {
  const statusOptions = [
    { value: 'present', label: 'Vorhanden', icon: '☑' },
    { value: 'partial', label: 'Teilweise erfüllt', icon: '☐' },
    { value: 'missing', label: 'Nicht erfüllt', icon: '☐' },
  ];

  const statusLabels: Record<string, string> = {
    datenschutzorganisation: 'Datenschutzorganisation',
    verarbeitungsverzeichnis: 'Verzeichnis von Verarbeitungstätigkeiten',
    rechtsgrundlagen: 'Rechtsgrundlagen der Verarbeitung',
    tom: 'Technische und organisatorische Maßnahmen',
    auftragsverarbeitung: 'Auftragsverarbeitung',
    betroffenenrechte: 'Betroffenenrechte',
    schulungen: 'Schulungen & Sensibilisierung',
  };

  return (
    <Stack spacing={3}>
      {/* Grundinformationen */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <h3 className="font-bold">Grundinformationen</h3>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Unternehmensname"
              value={data.companyName}
              onChange={(e) => onFieldChange('companyName', e.target.value)}
            />
            <TextField
              fullWidth
              label="Organisationseinheit(en)"
              value={data.organizationalUnits}
              onChange={(e) => onFieldChange('organizationalUnits', e.target.value)}
              placeholder="z.B. gesamtes Unternehmen / Abteilung"
            />
            <TextField
              fullWidth
              label="Anschrift"
              value={data.companyAddress}
              onChange={(e) => onFieldChange('companyAddress', e.target.value)}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="Prüfzeitraum"
              value={data.auditPeriod}
              onChange={(e) => onFieldChange('auditPeriod', e.target.value)}
              placeholder="z.B. 01.01.2025 - 31.12.2025"
            />
            <TextField
              fullWidth
              label="Datum des AuditReports"
              value={data.reportDate}
              onChange={(e) => onFieldChange('reportDate', e.target.value)}
              type="date"
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* DSB Information */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <h3 className="font-bold">Datenschutzbeauftragter</h3>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Name"
              value={data.dsbName}
              onChange={(e) => onFieldChange('dsbName', e.target.value)}
            />
            <TextField
              fullWidth
              label="Firma"
              value={data.dsbCompany}
              onChange={(e) => onFieldChange('dsbCompany', e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel>Art</InputLabel>
              <Select value={data.dsbType} label="Art" onChange={(e) => onFieldChange('dsbType', e.target.value)}>
                <MenuItem value="intern">Intern</MenuItem>
                <MenuItem value="extern">Extern</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Kontakt"
              value={data.dsbContact}
              onChange={(e) => onFieldChange('dsbContact', e.target.value)}
              placeholder="E-Mail / Telefon"
            />
            <TextField
              fullWidth
              label="Bestelldatum"
              value={data.dsbAppointmentDate}
              onChange={(e) => onFieldChange('dsbAppointmentDate', e.target.value)}
              type="date"
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Weitere Mandanteninformationen */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <h3 className="font-bold">Weitere Mandanteninformationen</h3>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Rechtsform"
              value={data.legalForm}
              onChange={(e) => onFieldChange('legalForm', e.target.value)}
              placeholder="z.B. GmbH"
            />
            <TextField
              fullWidth
              label="Branche"
              value={data.industry}
              onChange={(e) => onFieldChange('industry', e.target.value)}
            />
            <TextField
              fullWidth
              label="Mitarbeiterzahl"
              value={data.employeeCount}
              onChange={(e) => onFieldChange('employeeCount', e.target.value)}
              placeholder="ca. Anzahl"
            />
            <TextField
              fullWidth
              label="Verantwortliche Person(en)"
              value={data.responsiblePerson}
              onChange={(e) => onFieldChange('responsiblePerson', e.target.value)}
            />
            <TextField
              fullWidth
              label="Kontakt Verantwortlicher"
              value={data.responsibleContact}
              onChange={(e) => onFieldChange('responsibleContact', e.target.value)}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Aufsichtsbehörde */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <h3 className="font-bold">Zuständige Aufsichtsbehörde</h3>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Name der Aufsichtsbehörde"
              value={data.supervisoryAuthority}
              onChange={(e) => onFieldChange('supervisoryAuthority', e.target.value)}
              placeholder="z.B. Bayerisches Landesamt für Datenschutzaufsicht"
            />
            <TextField
              fullWidth
              label="Anschrift"
              value={data.supervisoryAuthorityAddress}
              onChange={(e) => onFieldChange('supervisoryAuthorityAddress', e.target.value)}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="Bundesland"
              value={data.supervisoryAuthorityState}
              onChange={(e) => onFieldChange('supervisoryAuthorityState', e.target.value)}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Statusübersicht */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <h3 className="font-bold">Statusübersicht</h3>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            {Object.entries(data.statusChecks).map(([key, value]) => (
              <Stack key={key} spacing={1.5}>
                <FormControl fullWidth>
                  <InputLabel>{statusLabels[key] || key}</InputLabel>
                  <Select
                    value={value}
                    label={statusLabels[key] || key}
                    onChange={(e) => onNestedFieldChange('statusChecks', key, e.target.value)}
                  >
                    {statusOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.icon} {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Anmerkungen"
                  value={data.statusNotes[key] || ''}
                  onChange={(e) => onNestedFieldChange('statusNotes', key, e.target.value)}
                  multiline
                  rows={2}
                  size="small"
                />
              </Stack>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Detaillierte Bewertungen */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <h3 className="font-bold">Detaillierte Bewertungen der Themenbereiche</h3>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="4.1 Datenschutz-Management-System (DSMS)"
              value={data.sections.dsms}
              onChange={(e) => onNestedFieldChange('sections', 'dsms', e.target.value)}
              placeholder="Bewertung des Datenschutz-Management-Systems..."
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="4.2 Website und Social Media"
              value={data.sections.website}
              onChange={(e) => onNestedFieldChange('sections', 'website', e.target.value)}
              placeholder="Bewertung der Website und Social Media Nutzung..."
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="4.3 Auftragsverarbeitung"
              value={data.sections.auftragsverarbeitung}
              onChange={(e) => onNestedFieldChange('sections', 'auftragsverarbeitung', e.target.value)}
              placeholder="Bewertung der Auftragsverarbeitung..."
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="4.4 Technische und organisatorische Maßnahmen (TOM)"
              value={data.sections.tom}
              onChange={(e) => onNestedFieldChange('sections', 'tom', e.target.value)}
              placeholder="Bewertung der technischen und organisatorischen Maßnahmen..."
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="4.5 Informationspflichten"
              value={data.sections.informationspflichten}
              onChange={(e) => onNestedFieldChange('sections', 'informationspflichten', e.target.value)}
              placeholder="Bewertung der Informationspflichten..."
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="4.6 Videoüberwachung"
              value={data.sections.videoueberwachung}
              onChange={(e) => onNestedFieldChange('sections', 'videoueberwachung', e.target.value)}
              placeholder="Bewertung der Videoüberwachung..."
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="4.7 Mitarbeiter und Beschäftigtendatenschutz"
              value={data.sections.mitarbeiter}
              onChange={(e) => onNestedFieldChange('sections', 'mitarbeiter', e.target.value)}
              placeholder="Bewertung des Beschäftigtendatenschutzes..."
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="4.8 Verzeichnis von Verarbeitungstätigkeiten und Löschkonzept"
              value={data.sections.verzeichnis}
              onChange={(e) => onNestedFieldChange('sections', 'verzeichnis', e.target.value)}
              placeholder="Bewertung des Verzeichnisses und Löschkonzepts..."
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="4.9 Betroffenenrechte"
              value={data.sections.betroffenenrechte}
              onChange={(e) => onNestedFieldChange('sections', 'betroffenenrechte', e.target.value)}
              placeholder="Bewertung der Betroffenenrechte..."
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="4.10 Datenschutzverletzungen"
              value={data.sections.datenschutzverletzungen}
              onChange={(e) => onNestedFieldChange('sections', 'datenschutzverletzungen', e.target.value)}
              placeholder="Bewertung des Umgangs mit Datenschutzverletzungen..."
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="4.11 Datenschutz-Folgenabschätzung (DSFA)"
              value={data.sections.dsfa}
              onChange={(e) => onNestedFieldChange('sections', 'dsfa', e.target.value)}
              placeholder="Bewertung der DSFA-Prozesse..."
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="4.12 Stellung und Unabhängigkeit des Datenschutzbeauftragten"
              value={data.sections.dsbPosition}
              onChange={(e) => onNestedFieldChange('sections', 'dsbPosition', e.target.value)}
              placeholder="Bewertung der Stellung des DSB..."
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Fazit */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <h3 className="font-bold">Fazit</h3>
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Gesamteinschätzung"
            value={data.fazit}
            onChange={(e) => onFieldChange('fazit', e.target.value)}
            placeholder="Zusammenfassende Bewertung des Datenschutzniveaus..."
          />
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
